import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBiddingDetails,
  updateBidding,
  deleteBidding,
} from "sr/utils/api/corporate/biddings";
import { Bidding } from "sr/models/Bidding";
import { toast } from "react-toastify";
import { RequirementCommonHeader } from "app/pages/corporate/layout/RequirementCommonHeader";
import { MainWrapper } from "../components/MainWrapper";
import { StatusPill } from 'sr/helpers/ui-components/StatusPill'
import { MetaBox } from 'sr/helpers/ui-components/MetaBox'
import ContentWithLoader from 'sr/layout/common/ContentLoader'
import AttachmentList from "sr/helpers/ui-components/AttachmentList";
import { useIntl } from 'react-intl'
import { getPricingType } from 'sr/utils/api/masterData/getPricingType'
import { getPaymentTerms } from 'sr/utils/api/masterData/getPaymentTerms'

export const BiddingDetails = () => {
  const intl = useIntl();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bidding, setBidding] = useState<Bidding | null>(null);

  const [status, setStatus] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const fetchDetails = async () => {
    if (!id) return;
    setLoading(true);
    const res = await getBiddingDetails(id);
    if (res && res.success) {
      const data = res.data as Bidding;
      setBidding(data);
      setStatus(String((data as any)?.status || ""));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const [pricingLabelMap, setPricingLabelMap] = useState<Record<string, string>>({});
  const [termsLabelMap, setTermsLabelMap] = useState<Record<string, string>>({});
  useEffect(() => {
    const load = async () => {
      try {
        const p = await getPricingType();
        if (p?.success && Array.isArray(p.data)) {
          const m: Record<string, string> = {};
          p.data.forEach((i: any) => (m[i.value] = i.label));
          setPricingLabelMap(m);
        }
      } catch {}
      try {
        const t = await getPaymentTerms();
        if (t?.success && Array.isArray(t.data)) {
          const m: Record<string, string> = {};
          t.data.forEach((i: any) => (m[i.value] = i.label));
          setTermsLabelMap(m);
        }
      } catch {}
    };
    load();
  }, []);

  const handleWithdraw = async () => {
    if (!id || !bidding) return;
    if (!window.confirm("Are you sure you want to withdraw this bid?")) return;

    setSavingStatus(true);
    try {
      const b: any = bidding;
      const payload: any = {
        corporate_id: String(b?.corporate_id?._id || ''),
        company_id: String(b?.company_id?._id || ''),
        requirement_id: String(b?.requirement_id?._id || ''),
        description: b?.description,
        experience: b?.experience,
        documents: b?.documents,
        pricing_type: b?.pricing_type,
        payment_terms: b?.payment_terms,
        bid_amount: b?.bid_amount,
        status: "withdrawn",
      };

      const res = await updateBidding(String(id), payload);

      if (res && (res as any).success) {
        setStatus("withdrawn");
        setBidding({ ...(bidding as any), status: "withdrawn" });
        toast.success("Bid withdrawn successfully");
      } else {
        toast.error("Failed to withdraw bid");
      }
    } catch (err) {
      toast.error("Failed to withdraw bid");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this bid permanently?")) return;

    setDeleting(true);
    try {
      const res = await deleteBidding(id);
      if (res && (res as any).success) {
        toast.success("Bid deleted successfully");
        navigate("/projects");
      } else {
        toast.error("Failed to delete bid");
      }
    } catch (err) {
      toast.error("Failed to delete bid");
    } finally {
      setDeleting(false);
    }
  };

  const getReqLabel = (b: any) => b?.requirement_id?.title || "—";
  const getCorpLabel = (b: any) => b?.corporate_id?.corporate_name || "—";

  const SkeletonCard = () => (
    <div className="p-6 flex flex-col gap-6 rounded-[20px] mb-6 border border-2xl animate-pulse">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
          <div className="h-5 w-20 bg-gray-200 rounded-full"></div>
          <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-11 w-24 bg-gray-200 rounded-lg"></div>
      </div>
      <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
      <div className="h-4 w-1/4 bg-gray-200 rounded"></div>
    </div>
  );

  return (
    <MainWrapper>
      <div className="py-3">
        <RequirementCommonHeader
          title="Bidding Details"
          actions={
            status !== null && (
              <>
                {status === "pending" && (
                  <button
                    onClick={() => navigate(`/projects/bid-review/${id}`)}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    Edit
                  </button>
                )}

                {status === "withdrawn" ? (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="ml-2 px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50"
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                ) : (
                  <button
                    onClick={handleWithdraw}
                    disabled={savingStatus}
                    className="ml-2 px-4 py-2 text-sm rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition disabled:opacity-50"
                  >
                    {savingStatus ? "Withdrawing..." : "Withdraw"}
                  </button>
                )}
              </>
            )
          }
        />


        <ContentWithLoader loading={loading} content={<SkeletonCard />}>
          {!bidding ? (
            <div className="p-6">No details found.</div>
          ) : (
            <div className="p-6 flex flex-col gap-6 rounded-[20px] mb-6 border border-2xl">
              <div className="flex gap-2 justify-between">
                <div className="flex flex-col">
                  <div className="flex gap-4">
                    <h3 className="text-lg font-semibold text-[#0078ae]">
                      {String(getReqLabel(bidding))}
                    </h3>
                  </div>
                  {getCorpLabel(bidding) && (
                    <span className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                      {String(getCorpLabel(bidding))}
                    </span>
                  )}
                </div>
                <div>
                  <StatusPill status={status} />
                </div>
              </div>

              {bidding.description && (
                <p className="text-sm text-gray-600">{bidding.description}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <MetaBox label="Amount" value={String(bidding.bid_amount ?? "—")} />
                <MetaBox label="Pricing" value={
                  bidding?.pricing_type
                    ? intl.formatMessage({ id: pricingLabelMap[String(bidding.pricing_type)] || String(bidding.pricing_type), defaultMessage: String(bidding.pricing_type) })
                    : '—'
                } />
                <MetaBox label="Payment Terms" value={
                  bidding?.payment_terms
                    ? intl.formatMessage({ id: termsLabelMap[String(bidding.payment_terms)] || String(bidding.payment_terms), defaultMessage: String(bidding.payment_terms) })
                    : '—'
                } />
                <MetaBox
                  label="Created"
                  value={
                    bidding.createdAt
                      ? new Date(bidding.createdAt).toLocaleDateString()
                      : "—"
                  }
                />
              </div>
              {Array.isArray((bidding as any).documents) &&
                (bidding as any).documents.length > 0 && (
                  <AttachmentList items={(bidding as any).documents || []} />
                )}
            </div>
          )}
        </ContentWithLoader>
      </div>
    </MainWrapper>
  );
};

export default BiddingDetails;
